import * as React from "react";
import { useTranslation } from "react-i18next";
import { Play, Pause } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFotoOffline } from "@/lib/offline/fotosOffline";

/**
 * Reproductor mínimo: un botón de play/pausa y el tiempo. Nada más.
 *
 * El `<audio controls>` nativo traía barra de progreso, volumen y menú de tres
 * puntos para descargar, ocupaba una anchura mínima que no se puede estilar —por eso
 * en el móvil había que apilarlo debajo del título— y pintaba su propio fondo gris,
 * ajeno al resto de la app. Aquí solo hace falta oír la nota, así que sobra todo eso.
 */
export function MiniAudioPlayer({ src: srcRed, className }: { src: string; className?: string }) {
  const { t } = useTranslation();
  // La copia del teléfono si la hay (modo sin conexión); si no, la de siempre.
  const src = useFotoOffline(srcRed);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = React.useState(false);
  const [duration, setDuration] = React.useState<number | null>(null);
  // El truco de forzar la duración se hace UNA vez por audio: si no, el salto al
  // final y la vuelta al principio se retroalimentan.
  const durationProbedRef = React.useRef(false);

  // Cambiar de sesión reutiliza el componente: hay que volver al principio o el
  // reproductor se queda con el tiempo de la nota anterior.
  React.useEffect(() => {
    setPlaying(false);
    setDuration(null);
    durationProbedRef.current = false;
  }, [src]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      void el.play();
    } else {
      el.pause();
    }
  };

  const fmt = (secs: number) => {
    if (!Number.isFinite(secs)) return "--:--";
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };
  // Siempre lo que DURA la nota, no lo que va reproducido.
  const timeLabel = duration !== null ? fmt(duration) : "--:--";

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 shrink-0 rounded-full bg-white border border-[#b6dded] pl-1 pr-2.5 py-1",
        className,
      )}
    >
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? t("common.pause", "Pausa") : t("common.play", "Reproducir")}
        className="w-7 h-7 rounded-full bg-[#2E84DE] text-white flex items-center justify-center shrink-0 active:scale-95 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
      >
        {playing ? (
          <Pause className="w-3.5 h-3.5" />
        ) : (
          // El triángulo pesa a la izquierda; +1px lo centra a ojo.
          <Play className="w-3.5 h-3.5 translate-x-[1px]" />
        )}
      </button>
      <span className="text-[11px] font-semibold text-[#13314A] tabular-nums">
        {timeLabel}
      </span>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        className="hidden"
        onLoadedMetadata={(e) => {
          const el = e.currentTarget;
          // Las notas se graban en el navegador (webm/opus) y llegan SIN duración en
          // los metadatos: `duration` sale Infinity y se quedaba en «--:--» hasta darle
          // al play. El apaño conocido es saltar al final para obligar al navegador a
          // medirla; cuando la publica (durationchange) se vuelve al principio.
          if (!Number.isFinite(el.duration)) {
            if (!durationProbedRef.current) {
              durationProbedRef.current = true;
              el.currentTime = 1e101;
            }
            return;
          }
          setDuration(el.duration);
        }}
        onDurationChange={(e) => {
          const el = e.currentTarget;
          if (!Number.isFinite(el.duration)) return;
          setDuration(el.duration);
          if (durationProbedRef.current && el.currentTime > 0 && el.paused) el.currentTime = 0;
        }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={(e) => {
          setPlaying(false);
          e.currentTarget.currentTime = 0;
        }}
      />
    </div>
  );
}
